import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ShoppingCart, User, LogOut, SlidersHorizontal, Menu, X } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";

export const Navbar = () => {
  const { user, logout } = useAuth();
  const { items } = useCart();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);

  // Referme le menu mobile à chaque changement de page
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const studioLink = user?.role === "admin" || user?.role === "artist";

  return (
    <header className="fixed inset-x-0 top-0 z-50 border-b border-white/[0.08] bg-black/60 backdrop-blur-xl">
      <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-4 sm:px-8">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-3">
          <img src="/logo.png" alt="Logo La Console" className="h-9 w-9 border border-white/10 object-cover" />
          <span className={`whitespace-nowrap font-display font-black tracking-tight text-xs sm:text-base ${user ? "hidden min-[480px]:inline" : ""}`}>
            LA CONSOLE <span className="text-amber-500">DES ARTISTES</span>
          </span>
        </Link>
        <nav className="hidden items-center gap-8 text-xs uppercase tracking-[0.2em] text-zinc-400 md:flex">
          <Link to="/" data-testid="nav-home" className="transition-colors hover:text-amber-400">Accueil</Link>
          <Link to="/catalogue" data-testid="nav-catalog" className="transition-colors hover:text-amber-400">Catalogue</Link>
          <Link to="/artistes" data-testid="nav-artists" className="transition-colors hover:text-amber-400">Artistes</Link>
          <Link to="/journal" data-testid="nav-journal" className="transition-colors hover:text-amber-400">Journal</Link>
          <Link to="/contact" data-testid="nav-contact" className="transition-colors hover:text-amber-400">Contact</Link>
          {studioLink && (
            <Link to="/admin" data-testid="nav-admin" className="flex items-center gap-1.5 transition-colors hover:text-amber-400">
              <SlidersHorizontal className="h-3.5 w-3.5" /> Studio
            </Link>
          )}
        </nav>
        <div className="flex items-center gap-3">
          <Link
            to="/panier"
            data-testid="nav-cart"
            className="relative flex h-10 w-10 items-center justify-center border border-white/[0.08] transition-colors hover:border-amber-500/60 hover:text-amber-400"
          >
            <ShoppingCart className="h-4 w-4" />
            {items.length > 0 && (
              <span data-testid="nav-cart-count" className="absolute -right-1.5 -top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-amber-500 text-[10px] font-bold text-black">
                {items.length}
              </span>
            )}
          </Link>
          {user ? (
            <div className="flex items-center gap-3">
              <Link
                to="/compte"
                data-testid="nav-account"
                className="flex h-10 items-center gap-2 border border-white/[0.08] px-4 text-xs uppercase tracking-[0.2em] transition-colors hover:border-amber-500/60 hover:text-amber-400"
              >
                <User className="h-3.5 w-3.5" />
                <span className="hidden sm:inline">Compte</span>
              </Link>
              <button
                onClick={async () => { await logout(); navigate("/"); }}
                data-testid="nav-logout"
                className="flex h-10 w-10 items-center justify-center border border-white/[0.08] text-zinc-400 transition-colors hover:border-red-500/60 hover:text-red-400"
                title="Se déconnecter"
              >
                <LogOut className="h-4 w-4" />
              </button>
            </div>
          ) : (
            <Link
              to="/connexion"
              data-testid="nav-login"
              className="hidden h-10 items-center border border-amber-500/60 px-5 text-xs uppercase tracking-[0.2em] text-amber-400 transition-colors hover:bg-amber-500 hover:text-black sm:flex"
            >
              Connexion
            </Link>
          )}
          <button
            onClick={() => setMenuOpen((o) => !o)}
            data-testid="nav-burger"
            aria-label="Menu"
            className="flex h-10 w-10 items-center justify-center border border-white/[0.08] text-zinc-300 transition-colors hover:border-amber-500/60 hover:text-amber-400 md:hidden"
          >
            {menuOpen ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
          </button>
        </div>
      </div>
      {menuOpen && (
        <nav
          data-testid="nav-mobile-menu"
          className="border-t border-white/[0.08] bg-black/95 px-5 py-4 backdrop-blur-xl md:hidden"
        >
          <div className="flex flex-col gap-1 text-sm uppercase tracking-[0.2em] text-zinc-300">
            <Link to="/" data-testid="nav-mobile-home" className="border-b border-white/[0.05] py-3 transition-colors hover:text-amber-400">Accueil</Link>
            <Link to="/catalogue" data-testid="nav-mobile-catalog" className="border-b border-white/[0.05] py-3 transition-colors hover:text-amber-400">Catalogue</Link>
            <Link to="/artistes" data-testid="nav-mobile-artists" className="border-b border-white/[0.05] py-3 transition-colors hover:text-amber-400">Artistes</Link>
            <Link to="/journal" data-testid="nav-mobile-journal" className="border-b border-white/[0.05] py-3 transition-colors hover:text-amber-400">Journal</Link>
            <Link to="/contact" data-testid="nav-mobile-contact" className="border-b border-white/[0.05] py-3 transition-colors hover:text-amber-400">Contact</Link>
            {studioLink && (
              <Link to="/admin" data-testid="nav-mobile-admin" className="flex items-center gap-2 border-b border-white/[0.05] py-3 transition-colors hover:text-amber-400">
                <SlidersHorizontal className="h-3.5 w-3.5" /> Studio
              </Link>
            )}
            {!user && (
              <Link to="/connexion" data-testid="nav-mobile-login" className="py-3 font-bold text-amber-400 transition-colors hover:text-amber-300">
                Connexion
              </Link>
            )}
          </div>
        </nav>
      )}
    </header>
  );
};
